import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { fetchData, intl } from 'dmsbasereact';
import { DmsAutocomplete } from '../DmsAutoComplete/DmsAutoComplete';

const formatDate = (date) => {
    if (!date) return '-';
    try {
        return new Date(date).toLocaleDateString('pt-BR');
    } catch {
        return '-';
    }
};

export const DmsBatchSearch = ({
    organizationBranchPk,
    value,
    onChange,
    onBatchSelect,
    label,
    required = false,
    error,
    helperText,
    disabled = false,
    name = 'batch_pk',
}) => {
    const [batches, setBatches] = useState([]);
    const [loading, setLoading] = useState(false);

    const fetchBatches = useCallback(async (orgPk) => {
        if (!orgPk) {
            setBatches([]);
            return;
        }

        setLoading(true);
        try {
            const response = await fetchData(`/batch_location/institution/${orgPk}`, 'GET', true);
            if (response?.data) {
                const batchesData = (Array.isArray(response.data) ? response.data : []).map(item => ({
                    pk: item.batch_pk,
                    number: item.batch_number,
                    part_name: item.part_name,
                    quantity: item.quantity,
                    fabrication: item.fabrication,
                }));
                setBatches(batchesData);
            }
        } catch (err) {
            console.error('[DmsBatchSearch] Error loading batches:', err);
            setBatches([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchBatches(organizationBranchPk);
    }, [organizationBranchPk, fetchBatches]);

    const batchOptions = useMemo(() => {
        return batches.map(batch => ({
            value: batch.pk,
            label: `${intl.get("Batch").d("Batch")}: ${batch.number || '-'} | ${intl.get("Part").d("Part")}: ${batch.part_name || '-'} | ${intl.get("Quantity").d("Quantity")}: ${batch.quantity || 0} | ${intl.get("Fabrication").d("Fabrication")}: ${formatDate(batch.fabrication)}`,
        }));
    }, [batches]);

    const handleChange = useCallback((selectedValue) => {
        if (onChange) onChange(selectedValue);
        if (onBatchSelect) {
            const batch = batches.find(b => b.pk === selectedValue);
            onBatchSelect(batch || null);
        }
    }, [onChange, onBatchSelect, batches]);

    const displayLabel = label || (intl.get("Batch").d("Batch") + (required ? ' *' : ''));

    return (
        <DmsAutocomplete
            key={`batch-search-${batchOptions.length}`}
            name={name}
            label={displayLabel}
            value={value}
            onChange={handleChange}
            options={batchOptions}
            error={error}
            helperText={helperText}
            disabled={disabled || loading}
            fullWidth
        />
    );
};

DmsBatchSearch.propTypes = {
    organizationBranchPk: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func.isRequired,
    onBatchSelect: PropTypes.func,
    label: PropTypes.string,
    required: PropTypes.bool,
    error: PropTypes.bool,
    helperText: PropTypes.string,
    disabled: PropTypes.bool,
    name: PropTypes.string,
};
